import React, { useEffect, useState } from 'react';
import { ProfileImage } from '../ProfileImage/ProfileImage';

/**
 * WinnerAnimation - Themed celebration overlay for game victories and evictions
 *
 * Props:
 * - type: 'eviction' | 'victory' | 'glitch_caught' | 'glitch_escaped'
 * - player: { name, profile_image_url }
 * - theme: aiGeneratedTheme object with colors
 * - message: Custom message to display
 * - onComplete: Callback when animation finishes
 * - imposterTopic: (Glitch mode) the secret topic
 * - imposterPlayerName: (Glitch mode) who was The Glitch
 * - accusedPlayerName: (Glitch mode) who was accused
 * - accusedPlayerId: (Glitch mode) ID of the accused player
 * - humanPlayerId: ID of the human player viewing the animation
 */
const WinnerAnimation = ({ type, player, theme, message, onComplete, imposterTopic, imposterPlayerName, accusedPlayerName, accusedPlayerId, humanPlayerId }) => {
  const [confettiPieces, setConfettiPieces] = useState([]);

  const handleDismiss = () => {
    if (onComplete) {
      onComplete();
    }
  };

  const isEviction = type === 'eviction';
  const isVictory = type === 'victory';
  const isGlitchCaught = type === 'glitch_caught';
  const isGlitchEscaped = type === 'glitch_escaped';
  const isGlitch = isGlitchCaught || isGlitchEscaped;

  // Generate themed confetti colors
  const getConfettiColors = () => {
    if (isGlitchCaught) {
      return ['#22c55e', '#ffd700', '#66ffaa', '#ffffff'];
    }
    if (isGlitchEscaped) {
      return ['#ff3333', '#cc0000', '#880000', '#440000'];
    }

    if (!theme) {
      return ['#667eea', '#764ba2', '#f093fb', '#4facfe'];
    }

    if (isEviction) {
      return [
        theme.evil || '#8b0000',
        theme.secret || '#4a0e4e',
        theme.accent || '#667eea',
        '#000000'
      ];
    }

    // Bright, celebratory colors for victories
    return [
      theme.accent || '#ffd700',
      theme.highlight || '#ffed4e',
      theme.textAccent || '#f093fb',
      '#ffffff'
    ];
  };

  // Generate confetti pieces
  useEffect(() => {
    const colors = getConfettiColors();
    const pieces = [];
    const count = (isVictory || isGlitchCaught) ? 80 : 50;

    for (let i = 0; i < count; i++) {
      pieces.push({
        id: i,
        color: colors[Math.floor(Math.random() * colors.length)],
        left: Math.random() * 100,
        delay: Math.random() * 3,
        duration: 3 + Math.random() * 2,
        size: 8 + Math.random() * 12,
        rotation: Math.random() * 360,
        shape: Math.random() > 0.5 ? 'rect' : 'circle'
      });
    }

    setConfettiPieces(pieces);

    // Auto-dismiss: 5s for evictions, 10s for victories/glitch results
    const timer = setTimeout(() => {
      if (onComplete) {
        onComplete();
      }
    }, (isEviction) ? 5000 : 10000);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, theme, isVictory, onComplete]);

  const overlayBg = isGlitchCaught
    ? 'linear-gradient(135deg, rgba(0,80,30,0.95) 0%, rgba(0,20,10,0.98) 100%)'
    : isGlitchEscaped
      ? 'linear-gradient(135deg, rgba(80,0,0,0.95) 0%, rgba(20,0,0,0.98) 100%)'
      : isEviction
        ? `linear-gradient(135deg, ${theme?.evil || '#8b0000'}dd 0%, ${theme?.bgDark || '#000000'}dd 100%)`
        : `linear-gradient(135deg, ${theme?.accent || '#ffd700'}dd 0%, ${theme?.highlight || '#ffed4e'}dd 100%)`;

  const iconEmoji = isGlitchCaught ? '🎯' : isGlitchEscaped ? '😈' : isEviction ? '💀' : '🏆';
  const titleText = isGlitchCaught ? 'THE GOLDEN GLITCH WAS CAUGHT!' : isGlitchEscaped ? 'THE GOLDEN GLITCH ESCAPED!' : isEviction ? 'EVICTED' : 'WINNER!';

  return (
    <div
      onClick={handleDismiss}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 100000,
        cursor: 'pointer',
        animation: 'fadeIn 0.3s ease-in-out',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'auto',
        padding: '20px',
        boxSizing: 'border-box',
      }}
    >
      {/* Confetti */}
      {confettiPieces.map(piece => (
        <div
          key={piece.id}
          style={{
            position: 'fixed',
            top: -20,
            left: `${piece.left}%`,
            width: piece.shape === 'circle' ? piece.size : piece.size * 0.6,
            height: piece.size,
            backgroundColor: piece.color,
            borderRadius: piece.shape === 'circle' ? '50%' : '2px',
            opacity: 0.8,
            animation: `confettiFall ${piece.duration}s linear ${piece.delay}s forwards`,
            transform: `rotate(${piece.rotation}deg)`,
            pointerEvents: 'none',
          }}
        />
      ))}

      {/* Central overlay */}
      <div
        style={{
          position: 'relative',
          background: overlayBg,
          backdropFilter: 'blur(10px)',
          borderRadius: 24,
          padding: '32px 24px',
          boxShadow: `0 20px 60px rgba(0, 0, 0, 0.5)`,
          border: `3px solid ${isGlitchCaught ? '#22c55e' : isGlitchEscaped ? '#ff3333' : isEviction ? theme?.evil : theme?.accent || '#ffd700'}`,
          textAlign: 'center',
          maxWidth: '90%',
          width: 480,
          boxSizing: 'border-box',
          animation: 'scaleIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
        }}
      >
        {/* Close button */}
        <button
          onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: 'rgba(255, 255, 255, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            borderRadius: '50%',
            width: 32,
            height: 32,
            fontSize: '1.1rem',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1
          }}
        >
          ✕
        </button>

        {/* Icon */}
        <div
          style={{
            fontSize: '5rem',
            marginBottom: 16,
            lineHeight: 1,
            width: '1em',
            height: '1em',
            margin: '0 auto 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: (isVictory || isGlitchCaught) ? 'bounce 0.6s ease-in-out infinite alternate' : 'pulse 1.5s ease-in-out infinite'
          }}
        >
          {iconEmoji}
        </div>

        {/* Title */}
        <h1
          style={{
            fontSize: 'clamp(1.4rem, 5vw, 3rem)',
            fontWeight: 800,
            color: '#ffffff',
            textShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
            marginBottom: 24,
            letterSpacing: '0.05em',
            wordBreak: 'break-word',
          }}
        >
          {titleText}
        </h1>

        {/* Player info */}
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              width: 120,
              height: 120,
              margin: '0 auto 16px',
              borderRadius: '50%',
              overflow: 'hidden',
              border: `4px solid ${isGlitchCaught ? '#22c55e' : isGlitchEscaped ? '#ff3333' : isEviction ? theme?.evil : theme?.accent || '#ffd700'}`,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
              animation: (isVictory || isGlitchCaught) ? 'wiggle 0.5s ease-in-out infinite' : 'none'
            }}
          >
            <ProfileImage
              src={player?.profile_image_url}
              alt={player?.name}
              size={120}
            />
          </div>
          <h2
            style={{
              fontSize: '2rem',
              fontWeight: 600,
              color: '#ffffff',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
              marginBottom: 8
            }}
          >
            {player?.name}
          </h2>
        </div>

        {/* Message */}
        {message && !isGlitch && (
          <p
            style={{
              fontSize: '1.25rem',
              color: '#ffffff',
              fontStyle: 'italic',
              maxWidth: 400,
              margin: '0 auto',
              textShadow: '0 2px 6px rgba(0, 0, 0, 0.5)'
            }}
          >
            {message}
          </p>
        )}

        {/* Glitch mode details */}
        {isGlitch && (
          <div style={{ marginTop: 8, textAlign: 'center' }}>
            <div style={{
              fontSize: '1.1rem',
              color: 'rgba(255,255,255,0.7)',
              marginBottom: 12,
            }}>
              {isGlitchCaught
                ? `${imposterPlayerName || player?.name} was The Golden Glitch — the group figured it out!`
                : accusedPlayerName
                  ? `Your team accused ${accusedPlayerId && accusedPlayerId === humanPlayerId ? `${accusedPlayerName} (you)` : accusedPlayerName}, but they were innocent!`
                  : 'The vote was tied — no one was accused!'}
            </div>
            {imposterTopic && (
              <div style={{
                fontSize: '1.3rem',
                fontWeight: 700,
                color: '#66bbff',
                textShadow: '0 0 20px rgba(100,180,255,0.4)',
              }}>
                The topic was: "{imposterTopic}"
              </div>
            )}
            <div style={{
              marginTop: 16,
              fontSize: '1.5rem',
              fontWeight: 900,
              color: isGlitchCaught ? '#22c55e' : '#ff4444',
              letterSpacing: '2px',
            }}>
              {isGlitchCaught ? 'THE GROUP WINS!' : 'THE GOLDEN GLITCH WINS!'}
            </div>
          </div>
        )}

        {/* Tap to close hint */}
        <div style={{
          marginTop: 20,
          fontSize: '0.8rem',
          color: 'rgba(255,255,255,0.4)',
          letterSpacing: '1px',
        }}>
          Tap anywhere to close
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from {
            transform: scale(0.5);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes confettiFall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }

        @keyframes bounce {
          from {
            transform: translateY(0);
          }
          to {
            transform: translateY(-20px);
          }
        }

        @keyframes wiggle {
          0%, 100% {
            transform: rotate(-3deg);
          }
          50% {
            transform: rotate(3deg);
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }
      `}</style>
    </div>
  );
};

export default WinnerAnimation;
